/// Performance constants per aircraft type. Direct port of
/// FlatoutATC/Models/AircraftDatabase.swift (the
/// `AircraftTypeData` table). Six air types are supported; the
/// ground-only types (none currently) would go here too.
///
/// `normalLandingDecelRate` drives in-flight deceleration and the
/// landing flare — heavy aircraft slow much more slowly than light
/// or fighter, helicopters slow almost instantly.

import type { AircraftType } from '../types/LevelData'

export interface AircraftPerformance {
  /// Generic fallback prefix when the airport's country isn't in
  /// `countryPrefixes` (or when the spawn has no airport context,
  /// e.g. transition goals).
  flightNumberPrefix: string
  /// Country-code → array of operator/registration prefixes the
  /// callsign generator picks from when the spawning airport has a
  /// matching `country` field. Mirrors iOS's
  /// AircraftDatabase.swift `countryPrefixes` map.
  countryPrefixes?: Record<string, string[]>
  minSpeedKts: number
  maxSpeedKts: number
  minAltitudeFt: number
  maxAltitudeFt: number
  /// ft per minute
  normalClimbRate: number
  /// ft per minute
  normalDescentRate: number
  normalLandingSpeedKts: number
  /// knots/sec — used at takeoff to ramp from 0 to v2.
  takeoffAccelRate: number
  /// knots/sec — used in cruise/approach to ramp speed up or down.
  /// Heavies decelerate slowly (6); fighters drag through the air
  /// even slower in clean config (10 lookups feels brisk for them).
  /// Helicopters can stop on a dime (30).
  normalLandingDecelRate: number
  /// Rotation speed (begin lifting) in knots.
  rotationSpeed: number
  /// V2 / initial climb-out speed in knots.
  v2Speed: number
  /// Turn rate in degrees per second. Fixed-wing types share 3°/s
  /// (~standard rate turn). Helicopters yaw on the spot and run
  /// at ~6°/s for snappier player control.
  turnRateDegSec: number
}

// Country-code → registration-letter prefixes for general-aviation
// types (light, helicopter). Standard ICAO-ish call letters: N for
// US, PH for Netherlands, etc.
const GA_REG_PREFIXES: Record<string, string[]> = {
  US: ['N'],
  NL: ['PH'],
  DE: ['D'],
  FR: ['F'],
  GB: ['G'],
  JP: ['JA'],
  BR: ['PR', 'PT'],
  AT: ['OE'],
  ES: ['EC'],
  IT: ['I'],
  GR: ['SX'],
  ZA: ['ZS'],
  HK: ['B'],
  CN: ['B'],
}

// Airline three-letter operator prefixes used by regionalJet + heavy.
// Same list across both types per iOS.
const AIRLINE_PREFIXES: Record<string, string[]> = {
  US: ['AAL', 'DAL', 'UAL', 'SWA', 'JBU'],
  NL: ['KLM', 'TRA'],
  DE: ['DLH', 'EWG'],
  FR: ['AFR', 'EZY'],
  GB: ['BAW', 'EZY', 'RYR'],
  JP: ['JAL', 'ANA', 'JJP', 'APJ', 'SKY'],
  BR: ['GLO', 'AZU', 'TAM', 'LAN', 'PTB'],
  AT: ['AUA'],
  ES: ['IBE', 'VLG', 'AEA', 'IBS'],
  IT: ['ITY', 'NOS', 'VLE'],
  GR: ['AEE', 'OAL', 'GZN'],
  ZA: ['SAA', 'CAW', 'LNK', 'FLY'],
  HK: ['CPA', 'HKE', 'AHK'],
  CN: ['CCA', 'CSN', 'CES'],
}

const HEAVY_PREFIXES: Record<string, string[]> = {
  ...AIRLINE_PREFIXES,
  // Heavies bias toward freight + flag carriers.
  US: ['AAL', 'DAL', 'UAL', 'FDX', 'UPS'],
  JP: ['JAL', 'ANA', 'NCA'],
  BR: ['TAM', 'LAN', 'GLO'],
  HK: ['CPA', 'AHK'],
  ZA: ['SAA'],
}

const MILITARY_PREFIXES: Record<string, string[]> = {
  US: ['RCH', 'REACH', 'CONVOY'],
  NL: ['NAF'],
  DE: ['GAF'],
  FR: ['CTM'],
  GB: ['RRR'],
}

const FIGHTER_PREFIXES: Record<string, string[]> = {
  US: ['NVY', 'NAVY', 'TRIDENT'],
  NL: ['NAF'],
  DE: ['GAF'],
  FR: ['FAF'],
  GB: ['RRR'],
}

const LIGHT: AircraftPerformance = {
  flightNumberPrefix: 'GA',
  countryPrefixes: GA_REG_PREFIXES,
  minSpeedKts: 90,
  maxSpeedKts: 200,
  minAltitudeFt: 1000,
  // 16,000 ft service ceiling matches turbocharged singles (Cirrus
  // SR22, Beech Bonanza, Piper Saratoga) and light twins that operate
  // at mountain airports. Pre-bump this was 12,000 — fine at sea-
  // level fields but claustrophobic at KTEX (Telluride, field elev
  // 9,070 ft) where a player vectoring above the 11,000 ft survey
  // altitude had only 1,000 ft of headroom. Mirrors iOS commit
  // f644743 on branch 2.4. User feedback after the 2.3 ship.
  maxAltitudeFt: 16000,
  normalClimbRate: 700,
  normalDescentRate: 500,
  normalLandingSpeedKts: 70,
  takeoffAccelRate: 4.0,
  normalLandingDecelRate: 3.0,
  rotationSpeed: 55,
  v2Speed: 80,
  turnRateDegSec: 3.0,
}

const REGIONAL_JET: AircraftPerformance = {
  flightNumberPrefix: 'RJ',
  countryPrefixes: AIRLINE_PREFIXES,
  minSpeedKts: 140,
  maxSpeedKts: 280,
  minAltitudeFt: 1000,
  maxAltitudeFt: 18000,
  normalClimbRate: 1800,
  normalDescentRate: 1500,
  normalLandingSpeedKts: 140,
  takeoffAccelRate: 5.0,
  normalLandingDecelRate: 8.0,
  rotationSpeed: 55,
  v2Speed: 120,
  turnRateDegSec: 3.0,
}

const HEAVY: AircraftPerformance = {
  flightNumberPrefix: 'HVY',
  countryPrefixes: HEAVY_PREFIXES,
  minSpeedKts: 160,
  maxSpeedKts: 320,
  minAltitudeFt: 1000,
  maxAltitudeFt: 30000,
  normalClimbRate: 1500,
  normalDescentRate: 1200,
  normalLandingSpeedKts: 160,
  takeoffAccelRate: 3.5,
  normalLandingDecelRate: 6.0,
  rotationSpeed: 55,
  v2Speed: 170,
  turnRateDegSec: 3.0,
}

const MILITARY: AircraftPerformance = {
  flightNumberPrefix: 'MIL',
  countryPrefixes: MILITARY_PREFIXES,
  minSpeedKts: 250,
  maxSpeedKts: 400,
  minAltitudeFt: 1000,
  maxAltitudeFt: 30000,
  normalClimbRate: 4000,
  normalDescentRate: 3000,
  normalLandingSpeedKts: 160,
  takeoffAccelRate: 6.0,
  normalLandingDecelRate: 6.0,
  rotationSpeed: 55,
  v2Speed: 180,
  turnRateDegSec: 3.0,
}

const FIGHTER: AircraftPerformance = {
  flightNumberPrefix: 'NVY',
  countryPrefixes: FIGHTER_PREFIXES,
  minSpeedKts: 300,
  maxSpeedKts: 900,
  minAltitudeFt: 1000,
  maxAltitudeFt: 30000,
  normalClimbRate: 5000,
  normalDescentRate: 5000,
  normalLandingSpeedKts: 220,
  takeoffAccelRate: 10.0,
  normalLandingDecelRate: 10.0,
  rotationSpeed: 55,
  v2Speed: 250,
  turnRateDegSec: 3.0,
}

const HELICOPTER: AircraftPerformance = {
  flightNumberPrefix: 'HELI',
  countryPrefixes: GA_REG_PREFIXES,
  /// 0 = hover. Helicopters can sit in place at any altitude.
  minSpeedKts: 0,
  maxSpeedKts: 120,
  minAltitudeFt: 1000,
  /// 18,000 ft matches utility helicopters used in real mountain
  /// rescue / high-altitude ops — Bell 407 (18,690 ft service
  /// ceiling), Eurocopter AS350 B3 and EC130 (~23,000 ft). The
  /// previous 9,000 ft cap was below VNLK Lukla's field elevation
  /// (9,334 ft) and ruled out the Khumbu helipad goals (Tengboche
  /// 12,664 ft, Namche 11,286 ft) entirely. Mirrors iOS commit
  /// 6ede1c2 on branch 2.4.
  maxAltitudeFt: 18000,
  normalClimbRate: 500,
  normalDescentRate: 500,
  normalLandingSpeedKts: 60,
  takeoffAccelRate: 3.0,
  normalLandingDecelRate: 30.0,
  rotationSpeed: 55,
  v2Speed: 70,
  /// Helicopters yaw on the spot — twice the fixed-wing rate.
  turnRateDegSec: 6.0,
}

const TABLE: Record<AircraftType, AircraftPerformance> = {
  light: LIGHT,
  regionalJet: REGIONAL_JET,
  heavy: HEAVY,
  military: MILITARY,
  fighter: FIGHTER,
  helicopter: HELICOPTER,
}

export function performanceFor(type: AircraftType): AircraftPerformance {
  return TABLE[type] ?? REGIONAL_JET
}

/// Short label for the aircraft data tag — three letters keeps the
/// data block compact and consistent across types.
export function typeLabel(type: AircraftType): string {
  switch (type) {
    case 'light':
      return 'GA'
    case 'regionalJet':
      return 'RJ'
    case 'heavy':
      return 'HVY'
    case 'military':
      return 'MIL'
    case 'fighter':
      return 'FTR'
    case 'helicopter':
      return 'HEL'
  }
}
